import { GridsterItemConfig, ResolvedGridsterOptions } from '../types';

export const checkCollisionTwoItems = (item: GridsterItemConfig, item2: GridsterItemConfig): boolean =>
  item.x < item2.x + item2.cols &&
  item.x + item.cols > item2.x &&
  item.y < item2.y + item2.rows &&
  item.y + item.rows > item2.y;

export const checkCollisionTwoItemsForSwapping = (
  item: GridsterItemConfig,
  item2: GridsterItemConfig
): boolean => {
  const horizontalBoundaryItem1 = item.cols === 1 ? 0 : 1;
  const horizontalBoundaryItem2 = item2.cols === 1 ? 0 : 1;
  const verticalBoundaryItem1 = item.rows === 1 ? 0 : 1;
  const verticalBoundaryItem2 = item2.rows === 1 ? 0 : 1;

  return (
    item.x + horizontalBoundaryItem1 < item2.x + item2.cols &&
    item.x + item.cols > item2.x + horizontalBoundaryItem2 &&
    item.y + verticalBoundaryItem1 < item2.y + item2.rows &&
    item.y + item.rows > item2.y + verticalBoundaryItem2
  );
};

export const checkGridCollision = (
  item: GridsterItemConfig,
  options: ResolvedGridsterOptions
): boolean => {
  const noNegativePosition = item.y > -1 && item.x > -1;
  const maxGridCols = item.cols + item.x <= options.maxCols;
  const maxGridRows = item.rows + item.y <= options.maxRows;
  const maxItemCols = item.maxItemCols ?? options.maxItemCols;
  const minItemCols = item.minItemCols ?? options.minItemCols;
  const maxItemRows = item.maxItemRows ?? options.maxItemRows;
  const minItemRows = item.minItemRows ?? options.minItemRows;
  const inColsLimits = item.cols <= maxItemCols && item.cols >= minItemCols;
  const inRowsLimits = item.rows <= maxItemRows && item.rows >= minItemRows;
  const minAreaLimit = item.minItemArea ?? options.minItemArea;
  const maxAreaLimit = item.maxItemArea ?? options.maxItemArea;
  const area = item.cols * item.rows;
  const inMinArea = minAreaLimit <= area;
  const inMaxArea = maxAreaLimit >= area;

  return !(
    noNegativePosition &&
    maxGridCols &&
    maxGridRows &&
    inColsLimits &&
    inRowsLimits &&
    inMinArea &&
    inMaxArea
  );
};

export const findItemWithItem = (
  item: GridsterItemConfig,
  items: GridsterItemConfig[]
): GridsterItemConfig | null => {
  for (const candidate of items) {
    if (candidate !== item && checkCollisionTwoItems(candidate, item)) {
      return candidate;
    }
  }
  return null;
};

export const getNextPossiblePosition = (
  newItem: GridsterItemConfig,
  items: GridsterItemConfig[],
  options: ResolvedGridsterOptions,
  dimensions: { columns: number; rows: number },
  startingFrom: { x?: number; y?: number } = {}
): boolean => {
  for (let y = startingFrom.y ?? 0; y < dimensions.rows; y++) {
    newItem.y = y;
    for (let x = startingFrom.x ?? 0; x < dimensions.columns; x++) {
      newItem.x = x;
      if (!checkGridCollision(newItem, options) && !findItemWithItem(newItem, items)) {
        return true;
      }
    }
  }

  const canAddToRows = options.maxRows >= dimensions.rows + newItem.rows;
  const canAddToColumns = options.maxCols >= dimensions.columns + newItem.cols;

  if (dimensions.rows <= dimensions.columns && canAddToRows) {
    newItem.y = dimensions.rows;
    newItem.x = 0;
    return true;
  }

  if (canAddToColumns) {
    newItem.x = dimensions.columns;
    newItem.y = 0;
    return true;
  }

  return false;
};

export const pixelsToPosition = (
  value: number,
  cellSize: number,
  maxItems: number,
  rounding: (value: number) => number
): number => {
  const position = rounding(value / cellSize);
  return Math.min(Math.max(position, 0), Math.max(maxItems - 1, 0));
};
